<template>
  <div
    id="settings-app"
    class="body-wrapper m-0 px-6 py-2"
  >
    <form class="max-w-xl">
      <div
        data-field-name="name"
        :class="{ 'has-error': appNameError }"
      >
        <label
          for="name"
          :class="labelClasses"
        >
          App Name
        </label>
        <input
          v-model="localApp.name"
          name="name"
          type="text"
        >
        <p
          v-if="appNameError"
          class="error-msg"
        >
          {{ appNameError }}
        </p>
      </div>
      <div
        data-field-name="slug"
        :class="{ 'has-error': appURLError }"
      >
        <label
          for="slug"
          :class="labelClasses"
        >
          App URL
        </label>
        <div
          id="url-overlay"
          class="app-url-overlay m-0 h-10 border-none flex items-center
            before:content-[attr(data-before)] before:text-base before:text-muted"
          :data-before="url"
        />
        <input
          id="app-overlay"
          v-model="localApp.slug"
          class="m-0 font-normal"
          name="slug"
          type="text"
        >
        <p
          v-if="appURLError"
          class="error-msg"
        >
          {{ appURLError }}
        </p>

        <CustomDomainAppSettingsMessage />
      </div>
      <div>
        <label
          for="description"
          :class="labelClasses"
        >
          App Description
        </label>
        <input
          v-model="localApp.description"
          name="description"
          type="text"
        >
      </div>
      <div>
        <label
          for="from_email"
          :class="labelClasses"
        >
          App Email Address
        </label>
        <input
          v-model="localApp.settings.from_email"
          name="from_email"
          type="text"
        >
        <p class="instructions not-italic text-sm text-subtle font-normal mb-0 mt-2">
          The "from" email address the app will use when sending any emails
        </p>
      </div>
      <div>
        <label
          for="technical_contact"
          :class="labelClasses"
        >App Technical Contact</label>
        <input
          v-model="localApp.settings.technical_contact"
          name="technical_contact"
          type="text"
        >
        <p class="instructions  not-italic text-sm text-subtle font-normal mb-0 mt-2">
          The email address that should receive technical notifications such as Google Maps API issues
        </p>
      </div>
      <div>
        <label
          for="user_timezone"
          :class="labelClasses"
        >
          Timezone
        </label>
        <select
          v-model="localApp.settings.timezone"
          name="user_timezone"
        >
          <option
            v-for="timezone in timezones"
            :key="timezone.region"
            :value="timezone.region"
          >
            <span v-if="timezone.region">
              (GMT{{ timezone.gmt_offset_hr }}) {{ timezone.region }}
            </span>
            <span v-else>
              -------------
            </span>
          </option>
        </select>
      </div>
      <div>
        <label
          for="timezone_dst"
          :class="labelClasses"
        >
          Include Daylight Saving?
        </label>
        <select
          v-model="localApp.settings.timezone_dst"
          name="timezone_dst"
        >
          <option value="0">
            No
          </option>
          <option value="3600">
            Yes, +1 hour during Daylight Saving Time
          </option>
        </select>
      </div>
      <div>
        <label :class="labelClasses">
          Language
        </label>
        <select
          v-model="language"
          name="language"
        >
          <option
            v-for="lang in languages"
            :key="lang.value"
            :value="lang.value"
          >
            {{ lang.name }}
          </option>
        </select>
      </div>

      <div class="flex">
        <a
          class="button medium save bg-gradient-primary rounded-lg border-0 p-3 text-base leading-4 font-medium ml-auto"
          data-cy="save-app-settings"
          @click.prevent="onSave"
        >
          Save Settings
        </a>
      </div>
    </form>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { getSupportedKnackTimezoneNames, getStandardTimezoneOffsetInMinutes, getFormattedStandardTimezoneOffset } from '@knack/time-helper';
import RequestUtils from '@/components/util/RequestUtils';
import { getLiveAppBaseUrl } from '@/lib/url-helper';
import CustomDomainAppSettingsMessage from '@/components/settings/app/CustomDomainAppSettingsMessage';

const timezones = getSupportedKnackTimezoneNames().map((knackTimezoneName) => {
  const offsetSeconds = getStandardTimezoneOffsetInMinutes(knackTimezoneName) * 1000;
  const offsetFormatted = getFormattedStandardTimezoneOffset(knackTimezoneName);
  return {
    region: knackTimezoneName,
    gmt_offset_hr: offsetFormatted,
    gmt_offset_s: String(offsetSeconds),
  };
});

// this will become a dashed line
timezones.push({ region: '', gmt_offset_hr: '', gmt_offset_s: '' });

const usTimezones = new Set([
  'Hawaii',
  'Alaska',
  'Pacific Time (US & Canada)',
  'Arizona',
  'Mountain Time (US & Canada)',
  'Central Time (US & Canada)',
  'Eastern Time (US & Canada)',
  'Indiana (East)',
]);

timezones.sort((a, b) => {
  // Primary sort - US regions come first, then the dashed line, then everything else
  // eslint-disable-next-line no-nested-ternary
  const primarySortA = (usTimezones.has(a.region) ? 0 : (a.region === '' ? 1 : 2));
  // eslint-disable-next-line no-nested-ternary
  const primarySortB = (usTimezones.has(b.region) ? 0 : (b.region === '' ? 1 : 2));

  // secondary sort by offset, and final tie-breaker is alphabetical sort
  return primarySortA - primarySortB
    || Number(a.gmt_offset_s) - Number(b.gmt_offset_s)
    || a.region.localeCompare(b.region);
});

export default {
  name: 'Settings',
  components: {
    CustomDomainAppSettingsMessage,
  },
  mixins: [
    RequestUtils,
  ],
  data() {
    return {
      localApp: JSON.parse(JSON.stringify(this.$store.getters.app.raw())),
      errors: {
        nameTaken: undefined,
        nameMissing: undefined,
        slugTaken: undefined,
        slugMissing: undefined,
      },

      languages: [
        {
          value: 'english', name: 'English',
        },
        {
          value: 'azerbaijani', name: 'Azerbaijani',
        },
        {
          value: 'czech', name: 'Czech',
        },
        {
          value: 'danish', name: 'Danish',
        },
        {
          value: 'dutch', name: 'Dutch',
        },
        {
          value: 'finnish', name: 'Finnish',
        },
        {
          value: 'french', name: 'French',
        },
        {
          value: 'german', name: 'German',
        },
        {
          value: 'hungarian', name: 'Hungarian',
        },
        {
          value: 'italian', name: 'Italian',
        },
        {
          value: 'norwegian', name: 'Norwegian',
        },
        {
          value: 'polish', name: 'Polish',
        },
        {
          value: 'brazilian', name: 'Portuguese (Brazilian)',
        },
        {
          value: 'romanian', name: 'Romanian',
        },
        {
          value: 'spanish', name: 'Spanish',
        },
        {
          value: 'swedish', name: 'Swedish',
        },
        {
          value: 'turkish', name: 'Turkish',
        },
      ],

      // timezone_selected: this.$store.state.app.get('settings').timezone,
      // timezone_dst_selected: this.$store.state.app.get('settings').timezone_dst,
      timezones,
    };
  },
  computed: {
    ...mapGetters([
      'accountSlug',
      'app',
      'domain',
    ]),
    url() {
      return `${getLiveAppBaseUrl()}/`;
    },
    language: {
      get() {
        return this.localApp.settings.language || 'english';
      },
      set(newVal) {
        this.localApp.settings.language = newVal;
      },
    },
    timezoneOffset() {
      return (this.timezones.filter((timezone) => timezone.region === this.localApp.settings.timezone))[0].gmt_offset_s;
    },
    appNameError() {
      return this.errors.nameTaken || this.errors.nameMissing;
    },
    appURLError() {
      return this.errors.slugTaken || this.errors.slugMissing;
    },
    labelClasses() {
      return 'text-default font-medium mb-2 leading-none';
    },
  },
  watch: {
    // eslint-disable-next-line func-names
    'localApp.settings.timezone': function () {
      this.localApp.settings.timezone_offset = this.timezoneOffset;
    },
    // eslint-disable-next-line func-names
    'localApp.name': function (newValue, oldValue) {
      if (newValue && !oldValue) {
        this.errors.nameMissing = undefined;
      }
      if (this.errors.nameTaken) {
        this.errors.nameTaken = undefined;
      }
    },
    // eslint-disable-next-line func-names
    'localApp.slug': function (newValue, oldValue) {
      if (newValue && !oldValue) {
        this.errors.slugMissing = undefined;
      }
      if (this.errors.slugTaken) {
        this.errors.slugTaken = undefined;
      }
    },
  },
  mounted() {
    const $urlOverlay = document.getElementById('url-overlay');
    const $appOverlay = document.getElementById('app-overlay');

    $urlOverlay.style['padding-right'] = 0;

    $appOverlay.classList.remove('pl-3');
    $appOverlay.style['padding-left'] = `${$urlOverlay.offsetWidth}px`;
  },
  created() {
    this.notifyErrors = true;
  },
  methods: {
    onSave() {
      // Dev Note: Don't use the appSlug getter in this function since it might be cached.
      // if the slug has changed we'll need to upate the URL on success below
      const slugHasChanged = (this.localApp.slug !== this.app.slug);
      const oldAppSlug = this.app.slug;

      this.commitRequest({
        validate: () => this.app.validate(this.localApp),
        request: () => this.app.update({
          description: this.localApp.description,
          name: this.localApp.name,
          settings: this.localApp.settings,
          slug: this.localApp.slug,
        }),
        onSuccess: () => {
          document.title = `${this.app.get('name')} - Knack Builder`;

          if (slugHasChanged) {
            const url = window.location.href.replace(
              `/${this.accountSlug}/${oldAppSlug}/`,
              `/${this.accountSlug}/${this.localApp.slug}/`,
            );

            setTimeout(() => {
              window.location.href = url;
            }, 300);
          }
        },
        onError: (errArr) => {
          const errorMessages = {
            nameTaken: 'This app name is already being used',
            slugTaken: 'This app URL is already being used',
            nameMissing: 'Give your app a name',
            slugMissing: 'Give your app a URL',
          };

          errArr.forEach((err) => {
            const errorMessage = typeof err === 'string' ? err : err.message;
            const keys = typeof err === 'string' ? ['nameTaken', 'slugTaken'] : ['nameMissing', 'slugMissing'];

            keys.forEach((key) => {
              // Check if the error message matches one of the error messages we're looking for
              if (errorMessages[key] === errorMessage) {
                this.notifyErrors = false;
                this.errors[key] = errorMessage;
              }
            });
          });
        },
      });
    },
  },
};
</script>

<style lang="scss">
#settings-app > * {
  max-width: 600px;
}

#app-overlay {
  font-weight: 600;
  margin-top: 0.063em;
}

#url-overlay {
  border: 1px solid transparent;
  border-color: transparent;
  box-sizing: border-box;
  color: transparent;
  font-size: 0.95em;
  font-weight: 600;
  line-height: 1.25em;
  overflow: hidden;
  padding: 0.5rem;
  pointer-events: none;
  position: absolute;
  transition: width 2s ease 0s;
  white-space: pre;
}

#url-overlay::before {
  color: #9E9EA6;
  content: attr(data-before);
  font-weight: 400;
}

#url-overlay::after {
  color: #9E9EA6;
  content: attr(data-after);
  font-weight: 400;
}

/* .instructions {
  color: #999;
  font-size: .95em;
  font-style: italic;
  line-height: 1.8em !important;
  margin: 0;
  padding: 0;
} */
</style>
